import type { Category } from "@/api/categories";
import type { RecurringRule } from "@/api/recurring";

/**
 * What the recurring rules add up to, per month and per year.
 *
 * A rule's amount is per *interval* — every two weeks, every quarter, once a
 * year — so the rules cannot be summed as they stand. Each one is first turned
 * into what it costs (or brings in) over a year, and a month is a twelfth of
 * that year. The year has 365 days and 52 weeks: a household budget's year,
 * not an astronomer's, and the same convention a German bank's "monatlich
 * umgerechnet" uses.
 *
 * Integer arithmetic throughout (`model.md` §2.2): the one division is done on
 * whole minor units and rounded half away from zero, so a payment and its
 * refund still cancel. The month is computed from the rule rather than from its
 * rounded year — "120 € a year" is 10 € a month, not 10 € plus whatever the
 * year's rounding left over.
 *
 * Totals are the sum of the per-rule figures as shown, so the column a reader
 * adds up by eye agrees with the total under it. Currencies are never mixed:
 * every total is keyed by currency.
 *
 * Only rules that are still running count — a paused rule or one whose last
 * payment is behind us is not a commitment any more.
 */

const PER_YEAR: Record<RecurringRule["interval_unit"], number> = {
  day: 365,
  week: 52,
  month: 12,
  year: 1,
};

/** n / d, rounded half away from zero, on integers only. */
export function divideRounded(n: number, d: number): number {
  const magnitude = Math.abs(n);
  const quotient = Math.floor(magnitude / d);
  const remainder = magnitude - quotient * d;
  const rounded = remainder * 2 >= d ? quotient + 1 : quotient;
  return n < 0 ? -rounded : rounded;
}

/** The rule's amount over a year. */
export function yearlyMinor(rule: RecurringRule): number {
  return divideRounded(rule.template_amount_minor * PER_YEAR[rule.interval_unit], rule.interval_count);
}

/** The rule's amount over an average month. */
export function monthlyMinor(rule: RecurringRule): number {
  return divideRounded(
    rule.template_amount_minor * PER_YEAR[rule.interval_unit],
    rule.interval_count * 12,
  );
}

/** Active, and not already past its last payment. */
export function isRunning(rule: RecurringRule, today: string): boolean {
  return rule.is_active && (!rule.end_date || rule.end_date >= today);
}

export interface RuleLine {
  rule: RecurringRule;
  monthly: number;
  yearly: number;
}

export interface CurrencyTotals {
  currency: string;
  monthlyIncome: number;
  monthlyOutgoing: number;
  yearlyOutgoing: number;
}

export interface CategoryLine {
  /** Undefined for the rules filed under no category. */
  categoryId: string | undefined;
  name: string | undefined;
  currency: string;
  monthly: number;
  yearly: number;
}

export interface RecurringTotals {
  income: RuleLine[];
  outgoing: RuleLine[];
  totals: CurrencyTotals[];
  /** Outgoing only, rolled up to the top-level category, largest first. */
  byCategory: CategoryLine[];
}

export function recurringTotals(
  rules: RecurringRule[],
  categories: Category[],
  today: string,
): RecurringTotals {
  // A subcategory's spend belongs to its parent, as in the report (`model.md`
  // §4.2): "Wohnen" is the answer, "Miete" and "Strom" are its detail.
  const topOf = new Map<string, Category>();
  for (const parent of categories) {
    topOf.set(parent.id, parent);
    for (const child of parent.children ?? []) topOf.set(child.id, parent);
  }

  const lines = rules
    .filter((rule) => isRunning(rule, today))
    .map((rule) => ({ rule, monthly: monthlyMinor(rule), yearly: yearlyMinor(rule) }))
    .sort((a, b) => Math.abs(b.monthly) - Math.abs(a.monthly));
  const income = lines.filter((line) => line.rule.template_amount_minor > 0);
  const outgoing = lines.filter((line) => line.rule.template_amount_minor < 0);

  const totals = new Map<string, CurrencyTotals>();
  const totalsOf = (currency: string) => {
    let entry = totals.get(currency);
    if (!entry) {
      entry = { currency, monthlyIncome: 0, monthlyOutgoing: 0, yearlyOutgoing: 0 };
      totals.set(currency, entry);
    }
    return entry;
  };
  for (const line of income) totalsOf(line.rule.template_currency_code).monthlyIncome += line.monthly;
  for (const line of outgoing) {
    const entry = totalsOf(line.rule.template_currency_code);
    entry.monthlyOutgoing += line.monthly;
    entry.yearlyOutgoing += line.yearly;
  }

  const byCategory = new Map<string, CategoryLine>();
  for (const line of outgoing) {
    const top = line.rule.template_category_id
      ? topOf.get(line.rule.template_category_id)
      : undefined;
    const currency = line.rule.template_currency_code;
    const key = `${top?.id ?? ""}|${currency}`;
    let entry = byCategory.get(key);
    if (!entry) {
      entry = { categoryId: top?.id, name: top?.name, currency, monthly: 0, yearly: 0 };
      byCategory.set(key, entry);
    }
    entry.monthly += line.monthly;
    entry.yearly += line.yearly;
  }

  return {
    income,
    outgoing,
    totals: [...totals.values()].sort((a, b) => a.currency.localeCompare(b.currency)),
    // Outgoing is negative, so the largest spend sorts first in ascending order.
    byCategory: [...byCategory.values()].sort((a, b) => a.monthly - b.monthly),
  };
}
